"use client";

import { useRef, useState } from "react";
import * as Yup from "yup";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import { useRouter } from "next/navigation";
import { ReCAPTCHA } from "@/lib/vendor";
import { Formik, Form, Field, ErrorMessage } from "formik";
import Button from "@/Components/Button";
import FormInput from "@/Components/pages/TechnologyPage/technologies-new/Quote/QuoteForm/FormInput";
import apiService from "@/common/services/apiService";
import { hideFormLoader, showFormLoader } from "@/store/loaderSlice";

interface RfpMiniFormValues {
  full_name: string;
  email: string;
  project_summary: string;
}

const ErrorMessageComponent = (props: any) => (
  <div className="text-red-500 font-medium text-xs mt-2" role="alert">
    {props.children}
  </div>
);

interface RfpMiniFormProps {
  heading?: string;
  buttonLabel?: string;
  className?: string;
}

const RfpMiniForm: React.FC<RfpMiniFormProps> = ({
  heading = "Request for Proposal",
  buttonLabel = "Submit",
  className,
}) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const reCaptchaRef = useRef<ReCAPTCHA>(null);
  const [isLoading, setIsLoading] = useState(false);
  const sitekey = process.env.NEXT_PUBLIC_RECAPTCHA_V3;

  const initialValues: RfpMiniFormValues = {
    full_name: "",
    email: "",
    project_summary: "",
  };

  const validationSchema = Yup.object({
    full_name: Yup.string().trim().required("Full name is required."),
    email: Yup.string()
      .trim()
      .email("Email is not valid.")
      .max(100, "Email is too long")
      .required("Email is required.")
      .matches(/^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/, "Invalid email format"),
    project_summary: Yup.string().required("Project description is required"),
  });

  const handleSubmit = async (
    values: RfpMiniFormValues,
    { resetForm, setSubmitting }: any,
  ) => {
    try {
      setIsLoading(true);
      dispatch(showFormLoader());

      const captchaToken = sitekey
        ? await reCaptchaRef.current?.executeAsync()
        : null;

      const form_data = new FormData();
      form_data.append("full_name", values.full_name);
      form_data.append("email", values.email);
      form_data.append("project_summary", values.project_summary);
      form_data.append("page_link", window.location.href);
      form_data.append("page_title", document.title ?? "Trootech");
      // Backend requires these fields, but this mini form doesn't collect them from the user.
      form_data.append("country_name", "N/A");
      form_data.append("contact_on", "N/A");
      if (captchaToken) {
        form_data.append("captcha_token_v3", captchaToken as string);
      }

      const lst: any = await apiService.multipart_post("proposal", form_data);
      if (lst?.data?.success) {
        resetForm();
        setSubmitting(false);
        reCaptchaRef?.current?.reset();
        router.push("/thank-you");
      } else {
        toast.error(lst?.data?.message ?? "Something went wrong");
      }
    } catch (error) {
      console.log("RfpMiniForm ~ handleSubmit ~ error:", error);
      toast.error("Something went wrong");
    } finally {
      dispatch(hideFormLoader());
      setIsLoading(false);
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      <Form className={className} aria-label="Request for Proposal Form">
        <h5 className="xl:text-[24px] xl:leading-[31.2px] lg:text-[20px] lg:leading-[26px] font-bold text-primary-text text-center mb-[24px]">
          {heading}
        </h5>

        <div className="flex flex-col gap-[16px]">
          <FormInput name="full_name" placeholder="Full name" required />
          <FormInput name="email" type="email" placeholder="Email" required />
          <div className="textarea input-group sm:col-span-2 mb-0 ">
            <Field
              as="textarea"
              name="project_summary"
              id="project_summary"
              className="input xl:min-h-[120px] lg:min-h-[120px] min-h-[120px]"
              placeholder=" "
              aria-label="Project description"
              aria-describedby="project_summary-error"
              aria-required="true"
            />
            <label
              htmlFor="project_summary"
              className="text-subtitle-5 font-regular"
            >
              Project description*
            </label>
            <ErrorMessage
              name="project_summary"
              component={ErrorMessageComponent}
              id="project_summary-error"
            />
          </div>
        </div>

        <div className="flex items-center md:justify-between justify-end md:flex-nowrap flex-wrap gap-6 mt-6">
          <Button
            type="submit"
            variant="small"
            className="my-0  w-full"
            loading={isLoading}
            label={buttonLabel}
          />
        </div>

        {sitekey && (
          <ReCAPTCHA
            style={{ display: "inline" }}
            size="invisible"
            ref={reCaptchaRef}
            sitekey={sitekey}
          />
        )}
      </Form>
    </Formik>
  );
};

export default RfpMiniForm;
